import {
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import {
  BookCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Maximize,
  RefreshCw,
  ScanFace,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { SUPPORT_EMAIL } from '@/lib/contact';
import type { StorybookBook, StorybookProofReason, StorybookProofRedrawRequest } from '@/lib/storybook-api';
import {
  DEFAULT_PROOF_ROUNDS,
  STORYBOOK_PROOF_REASONS,
  ZOOM_FIT,
  ZOOM_MAX,
  ZOOM_STEP,
  ZOOM_TAP,
  clampZoom,
  proofItems,
  proofRedrawRequest,
  zoomAround,
  type StorybookProofChoices,
  type StorybookProofItem,
  type StorybookProofVerdict,
  type StorybookZoomView,
} from '@/components/storybook/storybook-proof';

// ---------------------------------------------------------------------------
// The proof (docs/storybook-identity-lock.md §9): once every picture of a paid
// book is drawn, the parent checks each one — above all the faces — before
// anything is made or printed. Each picture is "Looks right" or "Redraw this
// one" (with an optional reason); the marked ones are drawn again as one
// redraw round, and "Approve and make my book" is the only way on to the PDF
// and the printer. Everything here is the family's own content: the page
// wraps it in ph-no-capture and the dialogs, portaled outside it, carry their
// own.
// ---------------------------------------------------------------------------

/** Share of the frame one arrow press moves the picture. */
const PAN_SHARE = 0.1;
/** A press that moves further than this (px) is a drag, not a tap. */
const TAP_SLOP = 6;

interface ProofZoomProps {
  src: string | null;
  alt: string;
  label: string;
  onMediaError?: () => void;
}

type Gesture = { startX: number; startY: number; moved: boolean; pinchDist: number; pinchScale: number };

function ProofZoom({ src, alt, label, onMediaError }: ProofZoomProps) {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<StorybookZoomView>(ZOOM_FIT);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<Gesture | null>(null);

  const frame = () => {
    const rect = frameRef.current?.getBoundingClientRect();
    return { w: rect?.width || 0, h: rect?.height || 0, left: rect?.left || 0, top: rect?.top || 0 };
  };
  const zoomTo = (s: number, qx?: number, qy?: number) =>
    setView((current) => {
      const { w, h } = frame();
      return zoomAround(current, s, qx ?? w / 2, qy ?? h / 2, w, h);
    });
  const panBy = (dx: number, dy: number) =>
    setView((current) => {
      const { w, h } = frame();
      return clampZoom({ ...current, tx: current.tx + dx, ty: current.ty + dy }, w, h);
    });
  const local = (event: PointerEvent<HTMLDivElement>) => {
    const { left, top } = frame();
    return { x: event.clientX - left, y: event.clientY - top };
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const { w, h } = frame();
    const step = Math.max(w, h) * PAN_SHARE || 24;
    const zoomed = view.s > 1;
    switch (event.key) {
      case '+':
      case '=':
        zoomTo(view.s * ZOOM_STEP);
        break;
      case '-':
      case '_':
        zoomTo(view.s / ZOOM_STEP);
        break;
      case '0':
        setView(ZOOM_FIT);
        break;
      // Arrows move the view over the picture; unzoomed they are left to the dialog's scrolling.
      case 'ArrowLeft':
        if (!zoomed) return;
        panBy(step, 0);
        break;
      case 'ArrowRight':
        if (!zoomed) return;
        panBy(-step, 0);
        break;
      case 'ArrowUp':
        if (!zoomed) return;
        panBy(0, step);
        break;
      case 'ArrowDown':
        if (!zoomed) return;
        panBy(0, -step);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const point = local(event);
    pointers.current.set(event.pointerId, point);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* not every browser (or jsdom) captures */
    }
    if (pointers.current.size === 1) {
      gesture.current = { startX: point.x, startY: point.y, moved: false, pinchDist: 1, pinchScale: view.s };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        startX: point.x,
        startY: point.y,
        moved: true,
        pinchDist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        pinchScale: view.s,
      };
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    const current = gesture.current;
    if (!previous || !current) return;
    const point = local(event);
    pointers.current.set(event.pointerId, point);
    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      zoomTo(current.pinchScale * (Math.hypot(a.x - b.x, a.y - b.y) / current.pinchDist), (a.x + b.x) / 2, (a.y + b.y) / 2);
      return;
    }
    if (!current.moved && Math.hypot(point.x - current.startX, point.y - current.startY) > TAP_SLOP) current.moved = true;
    if (current.moved) panBy(point.x - previous.x, point.y - previous.y);
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.delete(event.pointerId) || pointers.current.size > 0) return;
    const ended = gesture.current;
    gesture.current = null;
    if (!ended || ended.moved || event.type === 'pointercancel') return;
    // A tap: zoom in on that spot (a face), or back out to the whole picture.
    const point = local(event);
    if (view.s > 1) setView(ZOOM_FIT);
    else zoomTo(ZOOM_TAP, point.x, point.y);
  };

  const percent = Math.round(view.s * 100);
  return (
    <div className="space-y-2">
      <div
        ref={frameRef}
        role="group"
        tabIndex={0}
        aria-label={t('storybookPage.book.proof.large.frame', { label, percent })}
        data-testid="storybook-proof-zoom"
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={cn(
          'relative aspect-square w-full select-none overflow-hidden rounded-2xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          view.s > 1 ? 'cursor-grab touch-none' : 'cursor-zoom-in touch-pan-y',
        )}
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            draggable={false}
            onError={onMediaError}
            className="h-full w-full origin-top-left object-cover"
            style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.s})` }}
          />
        ) : (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
            {t('storybookPage.book.proof.missing')}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="rounded-full bg-card"
          onClick={() => zoomTo(view.s / ZOOM_STEP)}
          disabled={!src || view.s <= 1}
          aria-label={t('storybookPage.book.proof.large.zoomOut')}
        >
          <ZoomOut aria-hidden="true" />
        </Button>
        <span className="min-w-[3.5rem] text-center text-sm font-semibold tabular-nums" aria-live="polite">
          {t('storybookPage.book.proof.large.zoomLevel', { percent })}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="rounded-full bg-card"
          onClick={() => zoomTo(view.s * ZOOM_STEP)}
          disabled={!src || view.s >= ZOOM_MAX}
          aria-label={t('storybookPage.book.proof.large.zoomIn')}
        >
          <ZoomIn aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="rounded-full"
          onClick={() => setView(ZOOM_FIT)}
          disabled={view.s <= 1}
        >
          <Maximize aria-hidden="true" />
          {t('storybookPage.book.proof.large.fit')}
        </Button>
      </div>
      <p className="text-center text-xs text-muted-foreground">{t('storybookPage.book.proof.large.hint')}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One picture's answer: "Looks right" / "Redraw this one" (+ why)
// ---------------------------------------------------------------------------

const optionBase =
  'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-center text-sm font-semibold leading-tight transition-colors hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0';

interface ProofChoiceProps {
  idPrefix: string;
  label: string;
  verdict: StorybookProofVerdict | undefined;
  reason: StorybookProofReason | undefined;
  /** No redraw rounds left: only "Looks right" can be chosen. */
  noRounds: boolean;
  onVerdict: (verdict: StorybookProofVerdict) => void;
  onReason: (reason: StorybookProofReason) => void;
}

/** Radix radio groups: one Tab stop each, arrow keys between the options. */
function ProofChoice({ idPrefix, label, verdict, reason, noRounds, onVerdict, onReason }: ProofChoiceProps) {
  const { t } = useTranslation();
  const reasonLabelId = `${idPrefix}-reason`;
  return (
    <div className="space-y-3">
      <RadioGroupPrimitive.Root
        value={verdict ?? ''}
        onValueChange={(value) => onVerdict(value as StorybookProofVerdict)}
        aria-label={t('storybookPage.book.proof.choiceLabel', { label })}
        className="grid grid-cols-2 gap-2"
      >
        <RadioGroupPrimitive.Item
          value="ok"
          className={cn(optionBase, 'data-[state=checked]:border-emerald-600 data-[state=checked]:bg-emerald-50 data-[state=checked]:text-emerald-950')}
        >
          <Check aria-hidden="true" />
          {t('storybookPage.book.proof.looksRight')}
        </RadioGroupPrimitive.Item>
        <RadioGroupPrimitive.Item
          value="redraw"
          disabled={noRounds}
          className={cn(optionBase, 'data-[state=checked]:border-amber-500 data-[state=checked]:bg-amber-50 data-[state=checked]:text-amber-950')}
        >
          <RefreshCw aria-hidden="true" />
          {t('storybookPage.book.proof.redrawThis')}
        </RadioGroupPrimitive.Item>
      </RadioGroupPrimitive.Root>
      {verdict === 'redraw' && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-50 p-3 text-amber-950">
          <p id={reasonLabelId} className="text-xs font-semibold">
            {t('storybookPage.book.proof.reasonLabel')}
          </p>
          <RadioGroupPrimitive.Root
            value={reason ?? ''}
            onValueChange={(value) => onReason(value as StorybookProofReason)}
            aria-labelledby={reasonLabelId}
            className="mt-2 flex flex-wrap gap-2"
          >
            {STORYBOOK_PROOF_REASONS.map((option) => (
              <RadioGroupPrimitive.Item
                key={option}
                value={option}
                className={cn(
                  optionBase,
                  'min-h-10 bg-white px-3 py-1.5 text-xs font-medium text-foreground data-[state=checked]:border-amber-600 data-[state=checked]:bg-amber-500 data-[state=checked]:text-white',
                )}
              >
                {t(`storybookPage.book.proof.reasons.${option}`)}
              </RadioGroupPrimitive.Item>
            ))}
          </RadioGroupPrimitive.Root>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export interface StorybookProofProps {
  book: StorybookBook;
  choices: StorybookProofChoices;
  onChoicesChange: Dispatch<SetStateAction<StorybookProofChoices>>;
  /** A redraw or an approval is being sent. */
  busy: 'redraw' | 'approve' | null;
  /** Sends the marked pictures; resolves true once they are being drawn again. */
  onRedraw: (request: StorybookProofRedrawRequest) => Promise<boolean>;
  /** Approves the book; resolves true once it is being made. */
  onApprove: () => Promise<boolean>;
  onMediaError?: () => void;
  /** Shown under the heading (how everyone was drawn). */
  intro?: ReactNode;
}

export function StorybookProof({
  book,
  choices,
  onChoicesChange,
  busy,
  onRedraw,
  onApprove,
  onMediaError,
  intro,
}: StorybookProofProps) {
  const { t } = useTranslation();
  const items = useMemo(() => proofItems(book), [book]);
  // The large view keeps its picture while it closes (the dialog animates out).
  const [large, setLarge] = useState<{ index: number; open: boolean }>({ index: 0, open: false });
  const [confirming, setConfirming] = useState(false);

  const proof = book.proof || {};
  const maxRounds = Math.max(0, proof.maxRounds ?? DEFAULT_PROOF_ROUNDS);
  const roundsLeft = Math.max(0, proof.roundsLeft ?? maxRounds);
  const noRounds = roundsLeft <= 0;
  const printBought = Boolean(book.purchased?.print);

  // With no rounds left a "redraw" answer (from before) means nothing any more.
  const verdictOf = (key: string): StorybookProofVerdict | undefined => {
    const verdict = choices[key]?.verdict;
    return verdict === 'redraw' && noRounds ? undefined : verdict;
  };
  const request: StorybookProofRedrawRequest = noRounds ? { keys: [], reasons: {} } : proofRedrawRequest(items, choices);
  const flagged = request.keys.length;
  const checked = items.filter((item) => verdictOf(item.key)).length;

  const setVerdict = (key: string, verdict: StorybookProofVerdict) =>
    onChoicesChange((current) => ({
      ...current,
      [key]: verdict === 'redraw' ? { verdict, reason: current[key]?.reason } : { verdict },
    }));
  const setReason = (key: string, reason: StorybookProofReason) =>
    onChoicesChange((current) => ({ ...current, [key]: { verdict: 'redraw', reason } }));

  const labelOf = (item: StorybookProofItem) =>
    item.page === 0 ? t('storybookPage.book.proof.cover') : t('storybookPage.book.proof.page', { n: item.page });
  const altOf = (item: StorybookProofItem) =>
    item.page === 0
      ? t('storybookPage.book.reader.coverAlt')
      : t('storybookPage.book.reader.illustrationAlt', { n: item.page });

  const redrawLabel =
    busy === 'redraw'
      ? t('storybookPage.book.proof.sending')
      : flagged > 0
        ? t('storybookPage.book.proof.redrawButton', { count: flagged })
        : t('storybookPage.book.proof.redrawNone');

  const redraw = () => {
    if (!flagged || busy) return;
    void onRedraw(request);
  };
  const approve = async () => {
    if (busy) return;
    if (await onApprove()) setConfirming(false);
  };

  const largeIndex = Math.min(large.index, Math.max(0, items.length - 1));
  const largeItem = items[largeIndex] || null;
  const showLarge = (index: number) => setLarge({ index: Math.min(Math.max(0, index), items.length - 1), open: true });
  const supportHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t('storybookPage.book.proof.supportSubject', { id: book.id }))}`;

  return (
    <div className="space-y-6" data-testid="storybook-proof">
      <div className="glass-card mx-auto max-w-2xl p-6 text-center sm:p-8">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground">
          <ScanFace className="h-3.5 w-3.5" aria-hidden="true" />
          {t('storybookPage.book.proof.badge')}
        </span>
        <h1 className="mt-3 text-2xl font-semibold sm:text-3xl">{t('storybookPage.book.proof.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('storybookPage.book.proof.body')}</p>
      </div>

      {intro && <div className="mx-auto max-w-2xl">{intro}</div>}

      <ol className="ph-no-capture grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-label={t('storybookPage.book.proof.listAria')}>
        {items.map((item, index) => {
          const label = labelOf(item);
          const verdict = verdictOf(item.key);
          const headingId = `sb-proof-${item.key}`;
          return (
            <li
              key={item.key}
              aria-labelledby={headingId}
              data-testid="storybook-proof-item"
              data-key={item.key}
              className={cn(
                'flex min-w-0 flex-col rounded-3xl border bg-card p-3 shadow-sm sm:p-4',
                verdict === 'redraw' ? 'border-amber-500 shadow-[0_0_0_1px_rgb(245_158_11)]' : verdict === 'ok' ? 'border-emerald-600/60' : 'border-border',
              )}
            >
              <button
                type="button"
                onClick={() => showLarge(index)}
                aria-label={
                  item.page === 0
                    ? t('storybookPage.book.proof.openCover')
                    : t('storybookPage.book.proof.openPage', { n: item.page })
                }
                className="group relative block aspect-square w-full overflow-hidden rounded-2xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {item.image ? (
                  <img
                    src={item.image}
                    alt={altOf(item)}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] motion-reduce:transition-none"
                    onError={onMediaError}
                  />
                ) : (
                  <span className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
                    {t('storybookPage.book.proof.missing')}
                  </span>
                )}
                <span aria-hidden="true" className="absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white">
                  <ZoomIn className="h-4 w-4" />
                </span>
                {verdict && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold shadow',
                      verdict === 'ok' ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white',
                    )}
                  >
                    {verdict === 'ok' ? <Check className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    {verdict === 'ok' ? t('storybookPage.book.proof.looksRight') : t('storybookPage.book.proof.markedRedraw')}
                  </span>
                )}
              </button>
              <h2 id={headingId} className="mt-3 text-base font-semibold">
                {label}
              </h2>
              {item.text && (
                <p className="storybook-display mt-1 whitespace-pre-line break-words rounded-2xl bg-[#fffaf0] px-3 py-2 text-base leading-relaxed text-[#20294a]">
                  {item.text}
                </p>
              )}
              <div className="mt-auto pt-3">
                <ProofChoice
                  idPrefix={headingId}
                  label={label}
                  verdict={verdict}
                  reason={choices[item.key]?.reason}
                  noRounds={noRounds}
                  onVerdict={(next) => setVerdict(item.key, next)}
                  onReason={(next) => setReason(item.key, next)}
                />
              </div>
            </li>
          );
        })}
      </ol>

      <section
        aria-labelledby="sb-proof-actions-title"
        data-testid="storybook-proof-actions"
        className="mx-auto max-w-2xl rounded-3xl border border-border bg-card p-5 shadow-[var(--shadow-card)] sm:p-6"
      >
        <h2 id="sb-proof-actions-title" className="text-xl font-semibold">
          {t('storybookPage.book.proof.actionsTitle')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground" aria-live="polite">
          {t('storybookPage.book.proof.checked', { checked, total: items.length })}
          {flagged > 0 && ` · ${t('storybookPage.book.proof.flagged', { count: flagged })}`}
        </p>

        <div className="mt-4 space-y-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="h-auto min-h-12 w-full whitespace-normal rounded-full bg-card py-2.5 text-center leading-snug"
            onClick={redraw}
            disabled={busy !== null || flagged === 0}
            aria-label={redrawLabel}
            aria-describedby="sb-proof-rounds-left"
            data-testid="storybook-proof-redraw"
          >
            {busy === 'redraw' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
            <span className="flex flex-col">
              <span>{redrawLabel}</span>
              <span id="sb-proof-rounds-left" className="text-xs font-normal text-muted-foreground">
                {noRounds
                  ? t('storybookPage.book.proof.noRoundsShort')
                  : t('storybookPage.book.proof.roundsLeft', { count: roundsLeft, max: maxRounds })}
              </span>
            </span>
          </Button>

          {noRounds ? (
            <div
              className="rounded-2xl border border-amber-500/40 bg-amber-50 p-4 text-sm text-amber-950"
              data-testid="storybook-proof-rounds-used"
            >
              <p>{t('storybookPage.book.proof.roundsUsed', { max: maxRounds })}</p>
              <a
                href={supportHref}
                className="mt-2 inline-flex max-w-full items-center gap-1.5 break-all font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                {SUPPORT_EMAIL}
              </a>
            </div>
          ) : (
            flagged === 0 && <p className="text-xs text-muted-foreground">{t('storybookPage.book.proof.redrawHint')}</p>
          )}

          <Button
            type="button"
            size="lg"
            className="h-auto min-h-12 w-full whitespace-normal rounded-full py-3 text-center leading-snug"
            onClick={() => setConfirming(true)}
            disabled={busy !== null || flagged > 0}
            aria-describedby={flagged > 0 ? 'sb-proof-approve-blocked' : undefined}
            data-testid="storybook-proof-approve"
          >
            <BookCheck aria-hidden="true" />
            {t('storybookPage.book.proof.approve')}
          </Button>
          {flagged > 0 && (
            <p id="sb-proof-approve-blocked" className="text-xs font-medium text-amber-800">
              {t('storybookPage.book.proof.approveBlocked', { count: flagged })}
            </p>
          )}
        </div>
      </section>

      <Dialog open={large.open && largeItem !== null} onOpenChange={(open) => setLarge((current) => ({ ...current, open }))}>
        {/* Portaled outside the page's ph-no-capture container: it needs its own. */}
        <DialogContent
          className="ph-no-capture storybook-theme max-h-[94dvh] w-[calc(100%-1rem)] max-w-2xl overflow-y-auto rounded-2xl bg-background p-4 text-foreground sm:p-6"
          data-testid="storybook-proof-large"
        >
          {largeItem && (
            <>
              <DialogHeader className="pr-8">
                <DialogTitle>{labelOf(largeItem)}</DialogTitle>
                <DialogDescription>{t('storybookPage.book.proof.large.description')}</DialogDescription>
              </DialogHeader>
              <ProofZoom key={largeItem.key} src={largeItem.image} alt={altOf(largeItem)} label={labelOf(largeItem)} onMediaError={onMediaError} />
              {largeItem.text && (
                <p className="storybook-display whitespace-pre-line break-words rounded-2xl bg-[#fffaf0] px-4 py-3 text-base leading-relaxed text-[#20294a]">
                  {largeItem.text}
                </p>
              )}
              <ProofChoice
                idPrefix={`sb-proof-large-${largeItem.key}`}
                label={labelOf(largeItem)}
                verdict={verdictOf(largeItem.key)}
                reason={choices[largeItem.key]?.reason}
                noRounds={noRounds}
                onVerdict={(next) => setVerdict(largeItem.key, next)}
                onReason={(next) => setReason(largeItem.key, next)}
              />
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full bg-card"
                  onClick={() => showLarge(largeIndex - 1)}
                  disabled={largeIndex === 0}
                  aria-label={t('storybookPage.book.proof.large.prev')}
                >
                  <ChevronLeft aria-hidden="true" />
                  <span className="hidden sm:inline">{t('storybookPage.book.reader.prevShort')}</span>
                </Button>
                <p className="text-sm font-semibold tabular-nums">
                  {t('storybookPage.book.proof.large.position', { n: largeIndex + 1, total: items.length })}
                </p>
                <Button
                  type="button"
                  className="rounded-full"
                  onClick={() => showLarge(largeIndex + 1)}
                  disabled={largeIndex >= items.length - 1}
                  aria-label={t('storybookPage.book.proof.large.next')}
                >
                  <span className="hidden sm:inline">{t('storybookPage.book.reader.nextShort')}</span>
                  <ChevronRight aria-hidden="true" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirming} onOpenChange={(open) => busy !== 'approve' && setConfirming(open)}>
        <AlertDialogContent className="ph-no-capture storybook-theme w-[calc(100%-2rem)] rounded-2xl bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('storybookPage.book.proof.confirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {printBought ? t('storybookPage.book.proof.confirm.bodyPrint') : t('storybookPage.book.proof.confirm.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={busy === 'approve'} className="rounded-full">
              {t('storybookPage.book.proof.confirm.cancel')}
            </AlertDialogCancel>
            <Button className="rounded-full" onClick={() => void approve()} disabled={busy !== null}>
              {busy === 'approve' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <BookCheck aria-hidden="true" />}
              {busy === 'approve' ? t('storybookPage.book.proof.confirm.approving') : t('storybookPage.book.proof.confirm.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
